/**
 * Fire Assay Business Logic Utilities
 * Pure functions for fire assay calculations following Uncle Bob's Clean Code principles
 */

// weightedAverage removed - fire assay uses simple arithmetic average
import isNil from 'lodash/isNil'
import { 
  DEFAULT_MARGIN_OF_ERROR,
  DEFAULT_PGM_HAIRCUT_FACTOR,
  DEFAULT_SECONDARY_HAIRCUT_FACTOR,
  PGM_METALS,
} from './constants'
import { findHousePinWeightForBar } from './refiningLotPinState'
import { calculateAverage } from './statisticalCalculations'
import { extractDisplayWeight } from './weightUtils'

/**
 * Extract gross and net weights from a fire assay sample
 * Handles multiple formats: rich weight objects, string numbers, or numeric values
 * @param {Object} sample - Sample object with gross and net properties
 * @returns {Object} Object with grossWeight and netWeight in grams
 */
const extractSampleWeights = (sample) => {
  const grossWeight = extractDisplayWeight(sample.gross, 'grams') ?? 
                     (typeof sample.gross === 'string' ? parseFloat(sample.gross) : sample.gross) ?? 0
  const netWeight = extractDisplayWeight(sample.net, 'grams') ?? 
                   (typeof sample.net === 'string' ? parseFloat(sample.net) : sample.net) ?? 0
  return { grossWeight, netWeight }
}

/**
 * Calculate process weight for a bar (bar gross weight + house pin gross weight)
 * @param {Object} bar - Bar object with grossWeight
 * @param {Array} pins - Canonical pin rows for the lot
 * @returns {number} Process weight in troy ounces
 */
export const calculateProcessWeight = (bar, pins = []) => {
  const grossWeight = extractDisplayWeight(bar.grossWeight, 'troyOunces') || 0
  const pinWeight = extractDisplayWeight(findHousePinWeightForBar(bar, pins), 'troyOunces') || 0
  return grossWeight + pinWeight
}

/**
 * Check if fire results contain incomplete data
 * @param {Object} fireResults - Fire results object
 * @returns {boolean} True if any non-outlier sample has partial data
 */
export const hasIncompleteData = (fireResults) => {
  return Object.values(fireResults)
    .filter(sample => sample && typeof sample === 'object')
    .filter(sample => !sample.outlier)
    .some(sample => {
      // Use same logic as fireAssayValidation for consistency
      const grossValue = extractDisplayWeight(sample.gross, 'grams') ?? 
                        (typeof sample.gross === 'string' ? parseFloat(sample.gross) : sample.gross) ?? 0
      const netValue = extractDisplayWeight(sample.net, 'grams') ?? 
                      (typeof sample.net === 'string' ? parseFloat(sample.net) : sample.net) ?? 0
      
      const hasGross = grossValue > 0
      const hasNet = netValue > 0
      
      // Partial completion: has one but not the other
      return (hasGross && !hasNet) || (hasNet && !hasGross)
    })
}

/**
 * Calculate fire assay purity percentage for a bar (NULL-SAFE)
 * @param {Object} fireResults - Fire assay results for the bar
 * @param {number} marginOfError - Margin of error factor (fallback default)
 * @param {boolean} isPrimarySilver - Whether primary metal is silver
 * @returns {number|null} Primary metal purity percentage, or null if incomplete
 */
export const calculateFirePurity = (fireResults, marginOfError = DEFAULT_MARGIN_OF_ERROR, isPrimarySilver = false) => {
  // SAFETY CHECK: Return null for incomplete data
  if (hasIncompleteData(fireResults)) {
    return null
  }
  const purities = []
  const weights = []
  
  Object.values(fireResults)
    .filter(sample => sample && typeof sample === 'object')
    .filter(sample => !sample.outlier)
    .forEach(sample => {
      const { grossWeight, netWeight } = extractSampleWeights(sample)
      
      if (grossWeight > 0) {
        // Round each individual purity to match backend storage precision (0.8889 → 88.89%)
        // Backend stores as NUMERIC(5,4) on 0-1 scale = 2 decimals on percentage scale
        const rawPurity = (netWeight / grossWeight) * 100
        const roundedPurity = Number(rawPurity.toFixed(2))
        purities.push(roundedPurity)
        weights.push(grossWeight)
      }
    })
  
  if (purities.length === 0) return null // Changed from 0 to null
  
  const lowFirePct = Math.min(...purities)
  // Use simple arithmetic average for cuppelation results (not weighted by sample size)
  const avgFirePct = purities.reduce((sum, purity) => sum + purity, 0) / purities.length
  
  return isPrimarySilver 
    ? avgFirePct * marginOfError
    : Math.max(lowFirePct, avgFirePct * marginOfError)
}

/**
 * Calculate metal ounces (Au/Ag) based on fire assay results
 * @param {number} processWeight - Process weight in troy ounces
 * @param {Object} fireAssayResults - Fire assay results with primaryPct and secondaryPct
 * @param {boolean} isPrimarySilver - Whether primary metal is silver
 * @returns {Object} Object with auOz and agOz properties
 */
export const calculateMetalOunces = (processWeight, fireAssayResults, isPrimarySilver = false) => {
  if (!fireAssayResults || typeof fireAssayResults !== 'object') {
    throw new Error('fireAssayResults must be an object with primaryPct and secondaryPct')
  }
  if (!fireAssayResults.hasOwnProperty('primaryPct') || !fireAssayResults.hasOwnProperty('secondaryPct')) {
    throw new Error('fireAssayResults must be an object with primaryPct and secondaryPct')
  }
  if (typeof fireAssayResults.primaryPct !== 'number') {
    throw new Error('fireAssayResults.primaryPct must be a number')
  }
  if (!isNil(fireAssayResults.secondaryPct) && typeof fireAssayResults.secondaryPct !== 'number') {
    throw new Error('fireAssayResults.secondaryPct must be a number or null')
  }
  
  const { primaryPct, secondaryPct } = fireAssayResults
  
  return {
    auOz: isPrimarySilver 
      ? ((secondaryPct ?? 0) / 100) * processWeight  // Gold is secondary for silver assays
      : (primaryPct / 100) * processWeight,   // Gold is primary for gold assays
    agOz: isPrimarySilver 
      ? (primaryPct / 100) * processWeight     // Silver is primary for silver assays
      : ((secondaryPct ?? 0) / 100) * processWeight   // Silver is secondary for gold assays
  }
}

/**
 * Get lowest percentage for a specific metal from XRF results
 * @param {Array} metalResults - Array of XRF results for a specific metal
 * @returns {number} Lowest percentage found, or 0 if none
 */
export const getLowestMetalPercentage = (metalResults) => {
  if (!Array.isArray(metalResults)) return 0
  
  const validNumbers = metalResults.filter(value => typeof value === 'number')
  return validNumbers.length > 0 ? Math.min(...validNumbers) : 0
}

const positiveXrfReadings = (raw) => (raw || []).filter(r => r != null && !isNaN(r) && r > 0)

/** Average all readings; haircut only the minimum before averaging. */
const averageWithPgmHaircut = (readings, pgmHaircutFactor) => {
  if (readings.length === 0) return 0
  const minReading = Math.min(...readings)
  return readings.reduce((sum, reading) => {
    const adjusted = reading === minReading ? reading * pgmHaircutFactor : reading
    return sum + adjusted
  }, 0) / readings.length
}

/**
 * Calculate PGM ounces from XRF results (PT/PD/IR/RH/RU).
 * @param {Object} xrfResults - XRF results for the bar
 * @param {number} processWeight - Process weight in troy ounces
 * @param {number} pgmHaircutFactor - Applied to lowest XRF reading before averaging
 * @returns {Object} oz keyed as ptOz, pdOz, irOz, rhOz, ruOz
 */
export const calculatePgmOunces = (xrfResults, processWeight, pgmHaircutFactor = DEFAULT_PGM_HAIRCUT_FACTOR) => {
  const ounces = {}
  for (const metal of PGM_METALS) {
    const avgPct = averageWithPgmHaircut(
      positiveXrfReadings(xrfResults[metal]),
      pgmHaircutFactor,
    )
    ounces[`${metal.toLowerCase()}Oz`] = (avgPct / 100) * processWeight
  }
  return ounces
}

/**
 * Calculate copper ounces from XRF results
 * @param {Object} xrfResults - XRF results for the bar
 * @param {number} processWeight - Process weight in troy ounces
 * @returns {number} Copper ounces
 */
export const calculateCopperOunces = (xrfResults, processWeight) => {
  const cuPct = getLowestMetalPercentage(xrfResults.CU || [])
  return (cuPct / 100) * processWeight
}

/**
 * Calculate complete bar totals for all bars
 * @param {Array} bars - Array of bar objects
 * @param {Array} pins - Canonical pin rows for the lot
 * @param {Object} fireResults - Fire assay results by bar number
 * @param {Object} xrfResults - XRF results by bar number  
 * @param {Object} metadata - Metadata containing metalSymbol and marginOfError
 * @param {number} pgmHaircutFactor - PGM haircut factor
 * @param {number} secondaryHaircutFactor - Secondary metal haircut factor (fallback default)
 * @returns {Array} Array of bar total objects showing metal composition of process weight
 */
export const calculateBarTotals = (
  bars,
  pins = [],
  fireResults,
  xrfResults,
  metadata,
  pgmHaircutFactor = DEFAULT_PGM_HAIRCUT_FACTOR,
  secondaryHaircutFactor = DEFAULT_SECONDARY_HAIRCUT_FACTOR
) => {
  const marginOfError = parseFloat(metadata?.marginOfError) || DEFAULT_MARGIN_OF_ERROR
  const isPrimarySilver = metadata?.metalSymbol === 'AG'
  
  return bars.map(bar => {
    const processWeight = calculateProcessWeight(bar, pins)
    
    // NEW: use calculateFireAssayResults instead of calculateFirePurity
    const barFireResults = fireResults[bar.barNumber] || {}
    const barXrfResults = xrfResults[bar.barNumber] || {}
    
    // Always derive Ag for AU assays: if a doré control is present, force
    // useDoreCorrection=true for this bar regardless of stored flag.
    const normalizedBarFireResults = (() => {
      const control = barFireResults && barFireResults.control
      const hasDore = control && (control.gross || control.net)
      if (!hasDore) return barFireResults
      if (isPrimarySilver) return barFireResults
      // Gold assay with doré present → enable doré correction
      return { ...barFireResults, control: { ...(control || {}), useDoreCorrection: true } }
    })()

    const fireAssayResults = calculateFireAssayResults(
      normalizedBarFireResults,
      barXrfResults,
      marginOfError, 
      isPrimarySilver, 
      secondaryHaircutFactor
    )
    
    const { ptOz, pdOz } = calculatePgmOunces(barXrfResults, processWeight, pgmHaircutFactor)
    const cuOz = calculateCopperOunces(barXrfResults, processWeight)
    
    const { auOz, agOz } = calculateMetalOunces(processWeight, fireAssayResults, isPrimarySilver)
    
    // For silver assays, copper is displayed separately so subtract from other
    // For gold assays, copper is included in "other" category 
    const otherOz = isPrimarySilver 
      ? processWeight - (auOz + agOz + cuOz + ptOz + pdOz)
      : processWeight - (auOz + agOz + ptOz + pdOz)
    
    return {
      weight: processWeight,
      auOz,
      agOz,
      cuOz,
      ptOz,
      pdOz,
      otherOz
    }
  })
}

// ===== PHASE 2: Fire Results Separation and Helper Functions =====

/**
 * Separate fire results into sample cups and doré control
 * @param {Object} fireResults - Fire assay results object with sample keys
 * @returns {Object} Object with sampleCups array, doreResult, and hasDore boolean
 */
export const separateFireResults = (fireResults) => {
  if (!fireResults || typeof fireResults !== 'object') {
    throw new Error('fireResults must be an object')
  }
  
  const sampleCups = []
  let doreResult = null
  
  Object.entries(fireResults).forEach(([key, sample]) => {
    if (key === 'control') {  // exact match for doré cup
      doreResult = sample
    } else if (sample && typeof sample === 'object' && !sample.outlier) {
      sampleCups.push(sample)
    }
  })
  
  return { sampleCups, doreResult, hasDore: !!doreResult }
}

/**
 * Calculate sample cup purity using arithmetic average with margin of error
 * @param {Array} sampleCups - Array of sample cup objects with gross/net weights
 * @param {number} marginOfError - Margin of error factor (fallback default)
 * @param {boolean} isPrimarySilver - Whether primary metal is silver
 * @returns {number} Primary metal purity percentage
 */
export const calculateSampleCupPurity = (sampleCups, marginOfError = DEFAULT_MARGIN_OF_ERROR, isPrimarySilver = false) => {
  if (!Array.isArray(sampleCups) || sampleCups.length === 0) {
    return 0
  }
  
  const purities = []
  const weights = []
  
  sampleCups.forEach(sample => {
    const { grossWeight, netWeight } = extractSampleWeights(sample)
    
    if (grossWeight > 0) {
      // Round each individual purity to match backend storage precision (0.8889 → 88.89%)
      // Backend stores as NUMERIC(5,4) on 0-1 scale = 2 decimals on percentage scale
      const rawPurity = (netWeight / grossWeight) * 100
      const roundedPurity = Number(rawPurity.toFixed(2))
      purities.push(roundedPurity)
      weights.push(grossWeight)
    }
  })
  
  if (purities.length === 0) return 0
  
  const lowFirePct = Math.min(...purities)
  // Use simple arithmetic average for cuppelation results (not weighted by sample size)
  const avgFirePct = purities.reduce((sum, purity) => sum + purity, 0) / purities.length
  
  // Return raw average when marginOfError is 1.0 (used for separate calculation phases)
  if (marginOfError === 1.0) {
    return avgFirePct
  }
  
  return isPrimarySilver 
    ? avgFirePct * marginOfError
    : Math.max(lowFirePct, avgFirePct * marginOfError)
}

/**
 * Calculate doré correction factor matching database formula
 * @param {Object} doreResult - Doré result object with gross/net weights
 * @returns {number} Correction factor (1.0 - net/gross)
 */
export const calculateDoreCorrection = (doreResult) => {
  const { grossWeight, netWeight } = extractSampleWeights(doreResult)
  
  if (grossWeight === 0) {
    throw new Error('gross weight cannot be zero')
  }
  
  // Database formula: correction_factor = 1.0 - (net_weight / gross_weight)
  return 1.0 - (netWeight / grossWeight)
}

/**
 * Calculate doré purity percentage
 * @param {Object} doreResult - Doré result object with gross/net weights
 * @returns {number} Doré purity percentage
 */
export const calculateDorePurity = (doreResult) => {
  const { grossWeight, netWeight } = extractSampleWeights(doreResult)
  
  if (grossWeight === 0) {
    return 0
  }
  
  return (netWeight / grossWeight) * 100
}

/**
 * Get XRF percentage for a specific metal (used as fallback in fire assay purity calculations)
 * Uses average of all readings to match backend behavior for purity computations
 * @param {Object} xrfResults - XRF results object with metal arrays
 * @param {string} metal - Metal symbol to extract (e.g., 'Au', 'Ag')
 * @returns {number} Average percentage found, or 0 if none
 */
export const getXrfPercentage = (xrfResults, metal) => {
  if (!xrfResults || typeof xrfResults !== 'object') {
    return 0
  }
  const avg = calculateAverage(xrfResults[metal] || [])
  return avg != null ? avg : 0
}

// ===== PHASE 3: Main Fire Assay Calculation Logic =====

/**
 * Calculate silver assay results with doré correction
 * @param {number} samplePurity - Raw sample cup purity (no margin of error)
 * @param {Object} doreResult - Doré result object
 * @param {Array} sampleCups - Array of sample cup objects
 * @param {Object} xrfResults - XRF results object
 * @param {number} marginOfError - Margin of error factor
 * @param {number} secondaryHaircutFactor - Secondary metal haircut factor
 * @returns {Object} Object with correctedPrimaryPct and secondaryPct
 */
const calculateSilverAssayWithDore = (samplePurity, doreResult, sampleCups, marginOfError) => {
  const useDoreCorrection = doreResult.useDoreCorrection || false
  
  if (useDoreCorrection) {
    // Use correction factor formula
    const correctionFactor = calculateDoreCorrection(doreResult)
    const correctedPrimaryPct = (samplePurity + (correctionFactor * 100)) * marginOfError
    
    return { correctedPrimaryPct, secondaryPct: null }
  } else {
    // Conservative method: ignore doré, use only cups w/ margin of error
    const correctedPrimaryPct = calculateSampleCupPurity(sampleCups, marginOfError, true)
    
    return { correctedPrimaryPct, secondaryPct: null }
  }
}

/**
 * Calculate gold assay results with doré
 * Formula: AU assays use cup for gold, doré for silver differential
 * @param {number} samplePurity - Raw sample cup purity (average, no margin of error)
 * @param {Array} sampleCups - Array of sample cup objects (needed for conservative min)
 * @param {Object} doreResult - Doré result object
 * @param {Object} xrfResults - XRF results object
 * @param {number} marginOfError - Margin of error factor
 * @param {number} secondaryHaircutFactor - Secondary metal haircut factor
 * @returns {Object} Object with correctedPrimaryPct and secondaryPct
 */
const calculateGoldAssayWithDore = (samplePurity, sampleCups, doreResult, marginOfError, secondaryHaircutFactor) => {
  const useDoreCorrection = doreResult.useDoreCorrection || false
  const dorePurity = calculateDorePurity(doreResult)
  
  // AU ASSAYS: Gold ALWAYS from cup (doré doesn't measure gold, measures total precious metals)
  // Doré measures total precious metals (Au+Ag) - NOT directly comparable to cup gold
  
  // Primary gold always uses margin of error (no correction factor for gold assays)
  // Conservative mode: use max(min_cup, avg_cup × margin)
  // Matches backend: GREATEST(v_min_cup, v_avg_cup * v_margin_of_error)
  const cupPurities = sampleCups.map(sample => {
    const { grossWeight, netWeight } = extractSampleWeights(sample)
    return grossWeight > 0 ? (netWeight / grossWeight) * 100 : null
  }).filter(p => p !== null)
  
  const minCup = cupPurities.length > 0 ? Math.min(...cupPurities) : samplePurity
  const correctedPrimaryPct = Math.max(minCup, samplePurity * marginOfError)
  
  // Flag controls whether to derive secondary silver from doré differential
  // NOTE: useDoreCorrection means different things for gold vs silver assays:
  // - Gold: "derive secondary silver from doré" (doré is customer material, NOT pure silver control)
  // - Silver: "apply correction factor to primary silver" (doré is pure silver control shot)
  let secondaryPct = null
  if (useDoreCorrection && dorePurity !== null) {
    // Calculate secondary silver from doré differential
    // Doré measures total precious metals (Au+Ag), so silver = (doré - margin_adjusted_gold) × haircut
    // IMPORTANT: Use margin-adjusted cup purity to match backend calculation
    // Backend uses v_corrected_gold (which has margin applied) for the differential
    // NOTE: XRF and Fire Assay should never be mixed - doré differential uses only fire assay data
    const marginAdjustedCupPurity = samplePurity * marginOfError
    secondaryPct = Math.max(0, (dorePurity - marginAdjustedCupPurity) * secondaryHaircutFactor)
  }
  
  return { correctedPrimaryPct, secondaryPct }
}

/**
 * Calculate fire assay results with database-aligned logic
 * @param {Object} fireResults - Fire assay results by sample key
 * @param {Object} xrfResults - XRF results object with metal arrays
 * @param {number} marginOfError - Margin of error factor (default 0.995)
 * @param {boolean} isPrimarySilver - Whether primary metal is silver
 * @param {number} secondaryHaircutFactor - Secondary metal haircut factor (default 0.95)
 * @returns {Object} Object with primaryPct, secondaryPct, totalPreciousMetalsPct, usedDore
 */
export const calculateFireAssayResults = (fireResults, xrfResults = {}, marginOfError, isPrimarySilver, secondaryHaircutFactor) => {
  if (!fireResults || typeof fireResults !== 'object') {
    throw new Error('fireResults must be an object')
  }
  if (typeof marginOfError !== 'number') {
    throw new Error('marginOfError must be a number')
  }
  if (typeof secondaryHaircutFactor !== 'number') {
    throw new Error('secondaryHaircutFactor must be a number')
  }
  
  const { sampleCups, doreResult, hasDore } = separateFireResults(fireResults)
  
  // Calculate sample cup purity (WITHOUT margin of error initially)
  const samplePurity = calculateSampleCupPurity(sampleCups, 1.0, isPrimarySilver)
  
  let correctedPrimaryPct, secondaryPct
  
  if (hasDore) {
    const result = isPrimarySilver
      ? calculateSilverAssayWithDore(samplePurity, doreResult, sampleCups, marginOfError)
      : calculateGoldAssayWithDore(samplePurity, sampleCups, doreResult, marginOfError, secondaryHaircutFactor)
    
    correctedPrimaryPct = result.correctedPrimaryPct
    secondaryPct = result.secondaryPct
  } else {
    // Fallback: apply margin of error to sample purity
    correctedPrimaryPct = calculateSampleCupPurity(sampleCups, marginOfError, isPrimarySilver)
    secondaryPct = null
  }
  
  // XRF fallback for secondary metal when doré doesn't provide it (null, undefined, or 0)
  // For gold assays: secondary is silver (AG)
  // For silver assays: secondary is gold (AU) - cannot derive from doré (pure silver control)
  if (isNil(secondaryPct) || secondaryPct === 0) {
    const secondaryMetal = isPrimarySilver ? 'AU' : 'AG'
    const xrfSecondaryPct = getXrfPercentage(xrfResults, secondaryMetal)
    secondaryPct = xrfSecondaryPct > 0 ? xrfSecondaryPct : null
  }
  
  const totalPreciousMetalsPct = correctedPrimaryPct + (secondaryPct ?? 0)
  
  return { 
    primaryPct: correctedPrimaryPct, 
    secondaryPct, 
    totalPreciousMetalsPct, 
    usedDore: hasDore 
  }
}
